import { useId } from 'react'
import { cn } from './cnNew'
import { CheckIcon } from './iconsNew'

export function Field({ label, hint, children, className, htmlFor }) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label
          htmlFor={htmlFor}
          className="text-[12px] font-semibold tracking-wide text-muted-foreground uppercase"
        >
          {label}
        </label>
      )}
      {children}
      {hint && <p className="text-[12px] leading-relaxed text-muted-foreground">{hint}</p>}
    </div>
  )
}

export function Input({ className, size = 'md', ...props }) {
  const heights = { sm: 'h-[32px] text-[13px] px-[10px]', md: 'h-[40px] text-[14px] px-[12px]' }
  return (
    <input
      className={cn(
        'w-full rounded-lg border border-input bg-card text-foreground transition-colors',
        'placeholder:text-muted-foreground hover:border-ring/60 focus:border-ring focus:outline-none',
        heights[size],
        className
      )}
      {...props}
    />
  )
}

export function Checkbox({ checked, onChange, label, description, className, indeterminate = false }) {
  const id = useId()
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex cursor-pointer items-start gap-2.5 rounded-lg px-1.5 py-1.5 hover:bg-muted',
        className
      )}
    >
      <span className="relative flex h-[18px] w-[18px] shrink-0 items-center justify-center">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="peer sr-only"
        />
        <span
          aria-hidden="true"
          className={cn(
            'flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border transition-colors',
            checked || indeterminate
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-input bg-card',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-1'
          )}
        >
          {indeterminate && !checked ? (
            <span className="h-[2px] w-2.5 rounded-full bg-primary-foreground" />
          ) : (
            checked && <CheckIcon size={13} strokeWidth={3} />
          )}
        </span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] leading-5 text-foreground">{label}</span>
        {description && (
          <span className="block text-[12px] text-muted-foreground">{description}</span>
        )}
      </span>
    </label>
  )
}

export function Switch({ checked, onChange, label }) {
  const id = useId()
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2.5">
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={cn(
          'relative h-5 w-9 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-1',
          checked ? 'bg-primary' : 'bg-input'
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-4 w-4 rounded-full bg-card transition-all',
            checked ? 'left-[18px]' : 'left-0.5'
          )}
        />
      </span>
      {label && <span className="text-[13px] text-foreground">{label}</span>}
    </label>
  )
}

/** Grupo de botones tipo segmented control (una sola selección). */
export function Segmented({
  value,
  onChange,
  options,
  ariaLabel,
  className,
  size = 'sm',
  // Reparte el ancho entre las opciones (útil cuando ocupa toda la fila).
  stretch = false,
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'rounded-lg border border-input bg-muted p-0.5',
        stretch ? 'flex' : 'inline-flex',
        className
      )}
    >
      {options.map((option) => {
        const active = String(option.value) === String(value)
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            title={option.title || option.label}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap transition-colors',
              size === 'sm' ? 'h-[28px] px-[14px] text-[12px]' : 'h-[40px] px-[20px] text-[14px]',
              stretch && 'flex-1',
              active
                ? 'bg-card text-primary shadow-[0_1px_3px_rgba(16,42,48,0.12)]'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {option.icon}
            {option.label && <span>{option.label}</span>}
          </button>
        )
      })}
    </div>
  )
}

export function Badge({ children, tone = 'neutral', className }) {
  const tones = {
    neutral: 'bg-muted text-muted-foreground',
    primary: 'bg-primary-soft text-primary',
    success: 'bg-[color-mix(in_oklab,var(--success)_14%,white)] text-[var(--success)]',
    warning: 'bg-[color-mix(in_oklab,var(--warning)_16%,white)] text-[var(--warning)]',
    danger: 'bg-[color-mix(in_oklab,var(--destructive)_12%,white)] text-destructive',
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
        tones[tone],
        className
      )}
    >
      {children}
    </span>
  )
}
